import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepositoryMilestoneInternal} from '@github-ui/repository-milestone'
import {useEffect} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import {RepoIssuesSidebar} from '../components/sidebar/RepoIssuesSidebar'
import {useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {
  IssueIndexPage,
  IssueViewsPage,
  RepositoryLabelIndexPage,
  RepositoryMilestoneEditPage,
  RepositoryMilestoneIndexPage,
} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {RepositoryMilestonePageContentInternal$key} from './__generated__/RepositoryMilestonePageContentInternal.graphql'
import type {
  RepositoryMilestonePageQuery,
  RepositoryMilestonePageQuery$variables,
} from './__generated__/RepositoryMilestonePageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestonePageQuery($name: String!, $owner: String!, $number: Int!, $first: Int!, $query: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestonePageContentInternal @arguments(first: $first, number: $number, query: $query)
    }
  }
`

export const RepositoryMilestonePage: EntryPointComponent<
  {pageQuery: RepositoryMilestonePageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef, loadQuery} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Show">
      <RepositoryMilestoneContent pageQueryRef={queryRef} loadQuery={loadQuery} />
    </AnalyticsWrapper>
  )
}

export function RepositoryMilestoneContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestonePageQuery>
  loadQuery: (
    variables: RepositoryMilestonePageQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions | undefined,
  ) => void
}) {
  const pageData = usePreloadedQuery<RepositoryMilestonePageQuery>(PageQuery, pageQueryRef)

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueIndexPage.preload()
      if (isFeatureEnabled('issue_pinned_views')) {
        RepositoryMilestoneIndexPage.preload()
        RepositoryMilestoneEditPage.preload()
        RepositoryLabelIndexPage.preload()
        if (isFeatureEnabled('issue_pinned_views_index_page')) {
          IssueViewsPage.preload()
        }
      }
    })
  }, [])

  const {setCurrentViewId} = useQueryContext()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestonePageContentInternal repository={okResult.value} />
}

function RepositoryMilestonePageContentInternal({
  repository,
}: {
  repository: RepositoryMilestonePageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestonePageContentInternal on Repository
      @argumentDefinitions(first: {type: "Int!"}, number: {type: "Int!"}, query: {type: "String!"}) {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...RepositoryMilestoneInternal @arguments(first: $first, number: $number, query: $query)
      }
    `,
    repository,
  )

  const issuePinnedViewsEnabled = isFeatureEnabled('issue_pinned_views')
  if (issuePinnedViewsEnabled) {
    return (
      <RepositoryMilestoneInternal
        repository={data}
        leftPane={{
          element: <RepoIssuesSidebar />,
          ariaLabel: 'Issues sidebar navigation',
        }}
      />
    )
  }

  return <RepositoryMilestoneInternal repository={data} />
}
